import React, { useState, useEffect, useMemo, useRef } from "react";
import { ColorPicker, Select, message } from "antd";
import { useFormEditorContext } from "../../Contexts/FormEditorContext.jsx";
import { Slider } from "../ui/slider.tsx";
import font from "../../assets/icons/font-family.png";
import fontsize from "../../assets/icons/font-size.png";
import { Label } from "../ui/label.tsx";
import { Badge } from "../ui/badge.tsx";
import { Switch } from "../ui/switch.tsx";
import { ImagePlus, UploadCloud, Trash2 } from "lucide-react";
import { useUser } from "@clerk/clerk-react";
import { Button } from "../ui/button.tsx";
import FormSettingsModal from "../FormEditor/FormSettingsModal.tsx";
import { ImageCropModal } from "../ui/image-cropper.tsx";

// Constants for scaling
const DIV_SIZE = 12; // size-3 = 12px
const MAX_RADIUS = 30; // slider max value
const SCALE_FACTOR = 0.3; // This will ensure max radius of 9px for the preview div

const buttonSizes = [
    {
        value: "small",
        label: "Regular",
    },
    {
        value: "medium",
        label: "Medium",
    },
    {
        value: "large",
        label: "Large",
    },
];

const buttonTextStyles = [
    { value: false, label: "Normal" },
    { value: true, label: "Bold" },
];

const colorPickerStyles1 =
    " w-[107px]  border border-zinc-200 rounded-[8px] p-1 transition duration-150";
const colorPickerStyles2 =
    "flex w-full p-0 flex-row-reverse justify-between px-0.5 items-center border-none ";
const tabsStyles =
    "text-[#191B33] hover:text-zinc-950 border-transparent flex items-center justify-center py-1 font-medium rounded-[8px]";

const DesignTab = () => {
    const { theme, setTheme, setThemeChanged, setShowPricingModal, usersPlan } =
        useFormEditorContext();
    const fileInputRef = useRef(null);
    const [fontOptions, setFontOptions] = useState([]);
    const [textSize, setTextSize] = useState(theme.text_size || "small");
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const [buttonSize, setButtonSize] = useState(theme.button_size);
    const [fontFamily, setFontFamily] = useState(theme?.font_family);
    const [buttonRadius, setButtonRadius] = useState(theme?.rounded_corners);
    const [buttonTextbold, setButtonTextbold] = useState(theme?.is_bold);
    const [colors, setColors] = useState({
        background_color: theme?.background_color,
        text_color: theme?.text_color,
        button_color: theme?.button_color,
        button_text_color: theme?.button_text_color,
        answer_text_color: theme?.answer_text_color,
    });
    const [brandingImage, setBrandingImage] = useState(
        theme?.branding_logo_link,
    );
    const [brandingImageError, setBrandingImageError] = useState(null);

    const [showBuildformBranding, setShowBuildformBranding] = useState(
        theme?.show_buildform_branding,
    );

    const [showImageCropModal, setShowImageCropModal] = useState(false);
    //const [brandImage, setBrandImage] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);

    // Memoize the theme object to avoid unnecessary recalculations
    const memoizedTheme = useMemo(
        () => ({
            text_size: textSize,
            button_size: buttonSize,
            rounded_corners: buttonRadius,
            font_family: fontFamily,
            is_bold: buttonTextbold,
            show_buildform_branding: showBuildformBranding,
            branding_logo_link: brandingImage,
            ...colors,
        }),
        [
            textSize,
            buttonSize,
            buttonRadius,
            colors,
            fontFamily,
            buttonTextbold,
            showBuildformBranding,
            brandingImage,
        ],
    );

    useEffect(() => {
        setThemeChanged(true);
        setTheme(memoizedTheme);
    }, [memoizedTheme, setTheme, setThemeChanged]);

    const handleColorChange = (colorType, colorObject) => {
        const { r, g, b } = colorObject.metaColor;
        const rgbString = `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(
            b,
        )})`;
        setColors((prevColors) => ({
            ...prevColors,
            [colorType]: rgbString,
        }));
    };

    useEffect(() => {
        const fetchGoogleFonts = async () => {
            try {
                const response = await fetch(
                    `https://www.googleapis.com/webfonts/v1/webfonts?key=${process.env.REACT_APP_FONT_API_KEY}`,
                );
                const data = await response.json();
                const googleFonts = data.items.map((font) => ({
                    label: ` ${font.family}, ${font.category}`,
                    value: `${font.family}, ${font.category}`,
                    ...font,
                }));
                setFontOptions((prevOptions) => [
                    ...prevOptions,
                    ...googleFonts,
                ]);
            } catch (error) {
                console.error("Error fetching Google Fonts:", error);
            }
        };

        fetchGoogleFonts();
    }, []);

    const handleButtonSize = (size) => {
        setButtonSize(size);
    };

    const handleButtonRadius = (size) => {
        setTooltipVisible(true);
        const radius = size[0];
        setButtonRadius(radius);
    };
    const handleTextSize = (size) => {
        setTextSize(size);
    };

    const handleButtonTextBold = (value) => {
        setButtonTextbold(value);
    };

    const handleFontChange = async (value) => {
        try {
            const selectedFont = fontOptions?.find(
                (font) => font?.value === value,
            );
            if (!selectedFont) {
                message.error("Selected font not found in options");
                return;
            }

            setFontFamily(selectedFont);
        } catch (error) {
            console.error("Error loading font:", error);
        }
    };

    const handleRemoveBranding = (value) => {
        if (usersPlan !== "free") {
            setShowBuildformBranding(value);
        } else {
            setShowPricingModal(true);
        }
    };

    const handleFileInputClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (e) => {
        const file = e?.target?.files[0];

        setBrandingImageError(null); // Reset error state on new file selection

        /* if (!file) {
            setBrandingImageError("Please upload a file");
            return;
        } */
        console.log("File selected:", file);

        //file type validation
        const validFileTypes = ["image/png", "image/jpeg", "image/jpg"];
        if (!validFileTypes.includes(file.type)) {
            setBrandingImageError(
                "Invalid file type. Please upload a PNG or JPEG image.",
            );
            return;
        }

        if (file?.size > 1024 * 1024 * 7) {
            setBrandingImageError("File size must be less than 7MB");
            return;
        }

        if (file) {
            setSelectedFile(file);
            setShowImageCropModal(true);
        }
    };

    const handleRemoveCustomBrandingImage = () => {
        setBrandingImage(null);
    };

    const getScaledRadius = (radius) => {
        return Math.min(radius * SCALE_FACTOR, DIV_SIZE / 2);
    };

    return (
        <div className="h-screen overflow-y-scroll no-scrollbar">
            <ImageCropModal
                isOpen={showImageCropModal}
                setShowImageCropModal={setShowImageCropModal}
                selectedFile={selectedFile}
                setSelectedFile={setSelectedFile}
                setBrandingImage={setBrandingImage}
            />
            <div className="h-screen pb-8 mt-5 overflow-x-auto no-scrollbar">
                <div className="pb-4 border-b border-zinc-200">
                    <div className="flex items-center justify-between">
                        <SubHeading
                            title="Colors"
                            subTitle="Background and text colors."
                            icon={
                                <svg
                                    width="14"
                                    height="15"
                                    viewBox="0 0 14 15"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clip-path="url(#clip0_439_4068)">
                                        <path
                                            d="M7.87484 4.93327C8.03592 4.93327 8.1665 4.80268 8.1665 4.6416C8.1665 4.48052 8.03592 4.34993 7.87484 4.34993C7.71375 4.34993 7.58317 4.48052 7.58317 4.6416C7.58317 4.80268 7.71375 4.93327 7.87484 4.93327Z"
                                            stroke="#191B33"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                        <path
                                            d="M10.2082 7.2666C10.3693 7.2666 10.4998 7.13602 10.4998 6.97493C10.4998 6.81385 10.3693 6.68327 10.2082 6.68327C10.0471 6.68327 9.9165 6.81385 9.9165 6.97493C9.9165 7.13602 10.0471 7.2666 10.2082 7.2666Z"
                                            stroke="#191B33"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                        <path
                                            d="M4.95817 5.5166C5.11925 5.5166 5.24984 5.38602 5.24984 5.22493C5.24984 5.06385 5.11925 4.93327 4.95817 4.93327C4.79709 4.93327 4.6665 5.06385 4.6665 5.22493C4.6665 5.38602 4.79709 5.5166 4.95817 5.5166Z"
                                            stroke="#191B33"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                        <path
                                            d="M3.7915 8.43327C3.95259 8.43327 4.08317 8.30268 4.08317 8.1416C4.08317 7.98052 3.95259 7.84993 3.7915 7.84993C3.63042 7.84993 3.49984 7.98052 3.49984 8.1416C3.49984 8.30268 3.63042 8.43327 3.7915 8.43327Z"
                                            stroke="#191B33"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                        <path
                                            d="M6.99984 2.0166C3.7915 2.0166 1.1665 4.6416 1.1665 7.84993C1.1665 11.0583 3.7915 13.6833 6.99984 13.6833C7.54 13.6833 7.96117 13.2481 7.96117 12.6986C7.96117 12.4437 7.85617 12.2115 7.70625 12.0424C7.53709 11.8738 7.45075 11.662 7.45075 11.3861C7.44854 11.2577 7.4722 11.1302 7.52031 11.0111C7.56843 10.8921 7.64002 10.784 7.73081 10.6932C7.82161 10.6024 7.92975 10.5308 8.0488 10.4827C8.16785 10.4345 8.29537 10.4109 8.42375 10.4131H9.58809C11.3678 10.4131 12.8285 8.95302 12.8285 7.17327C12.8128 4.35693 10.1854 2.0166 6.99984 2.0166Z"
                                            stroke="#191B33"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_439_4068">
                                            <rect
                                                width="14"
                                                height="14"
                                                fill="white"
                                                transform="translate(0 0.849609)"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            }
                        />

                        <button
                            type="button"
                            aria-haspopup="dialog"
                            aria-expanded="false"
                            aria-controls="radix-:r2o:"
                            data-state="closed"
                            className="w-6 h-6 overflow-hidden rounded group"
                        >
                            <div className="grid w-12 h-12 grid-cols-2 overflow-hidden transition-all duration-300 ease-in-out rounded-xl -translate-x-1/4 -translate-y-1/4">
                                <span className="flex w-6 h-6 transition-all duration-300 ease-in-out group-hover:translate-x-full"></span>
                                <span className="flex w-6 h-6 transition-all duration-300 ease-in-out group-hover:translate-y-full"></span>
                                <span className="flex w-6 h-6 transition-all duration-300 ease-in-out group-hover:-translate-y-full"></span>
                                <span className="flex w-6 h-6 transition-all duration-300 ease-in-out group-hover:-translate-x-full"></span>
                            </div>
                        </button>
                    </div>
                    <div className="flex flex-col w-full gap-2">
                        <div className="flex items-center justify-between">
                            <p className="text-xs font-medium text-[#191B33]">
                                Background Color
                            </p>
                            <div className={colorPickerStyles1}>
                                <ColorPicker
                                    defaultValue={colors.background_color}
                                    className={colorPickerStyles2}
                                    showText
                                    onChange={(color) =>
                                        handleColorChange(
                                            "background_color",
                                            color,
                                        )
                                    }
                                />
                            </div>
                        </div>
                        <div className="flex items-center justify-between">
                            <p className="text-xs font-medium text-[#191B33]">
                                Text Color
                            </p>
                            <div className={colorPickerStyles1}>
                                <ColorPicker
                                    defaultValue={colors.text_color}
                                    className={colorPickerStyles2}
                                    showText
                                    onChange={(color) =>
                                        handleColorChange("text_color", color)
                                    }
                                />
                            </div>
                        </div>
                        <div className="flex items-center justify-between">
                            <p className="text-xs font-medium text-[#191B33]">
                                Button Color
                            </p>
                            <div className={colorPickerStyles1}>
                                <ColorPicker
                                    defaultValue={colors.button_color}
                                    className={colorPickerStyles2}
                                    showText
                                    onChange={(color) =>
                                        handleColorChange("button_color", color)
                                    }
                                />
                            </div>
                        </div>
                        <div className="flex items-center justify-between">
                            <p className="text-xs font-medium text-[#191B33]">
                                Button Text Color
                            </p>
                            <div className={colorPickerStyles1}>
                                <ColorPicker
                                    defaultValue={colors.button_text_color}
                                    className={colorPickerStyles2}
                                    showText
                                    onChange={(color) =>
                                        handleColorChange(
                                            "button_text_color",
                                            color,
                                        )
                                    }
                                />
                            </div>
                        </div>
                        <div className="flex items-center justify-between">
                            <p className="text-xs font-medium text-[#191B33]">
                                Answer Color
                            </p>
                            <div className={colorPickerStyles1}>
                                <ColorPicker
                                    defaultValue={colors?.answer_text_color}
                                    className={colorPickerStyles2}
                                    showText
                                    onChange={(color) =>
                                        handleColorChange(
                                            "answer_text_color",
                                            color,
                                        )
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="py-4 border-b border-zinc-200">
                    <SubHeading
                        title="Fonts"
                        subTitle="Pick a font that reflects your brand"
                        icon={<img src={font} alt="font" />}
                    />

                    <Select
                        showSearch
                        defaultValue={fontFamily}
                        style={{
                            width: "100%",
                        }}
                        className="border rounded-[8px] p-0.5 h-9 border-zinc-200"
                        onChange={handleFontChange}
                        filterOption={(input, option) =>
                            option.children
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                        }
                    >
                        {fontOptions?.map((font) => (
                            <Option
                                key={font.value}
                                value={font.value}
                                style={{ fontFamily: font.value.split(",")[0] }}
                            >
                                {font.label}
                            </Option>
                        ))}
                    </Select>
                </div>
                <div className="py-4 border-b border-zinc-200">
                    <SubHeading
                        title="Text Size"
                        subTitle="Change the size of the text"
                        icon={<img src={fontsize} alt="fontsize" />}
                    />
                    <div className="mt-4 rounded-lg bg-zinc-100">
                        <div className="grid grid-cols-2 p-1 h-9">
                            {buttonSizes
                                .filter((button) => button.value !== "large")
                                .map((button) => (
                                    <button
                                        key={button.value}
                                        className={` ${tabsStyles} ${
                                            button.value === textSize
                                                ? "bg-white"
                                                : ""
                                        }`}
                                        onClick={() =>
                                            handleTextSize(button.value)
                                        }
                                    >
                                        <p
                                            className={` text-xs ${
                                                button.value === textSize
                                                    ? "text-[#191B33]  font-semibold"
                                                    : " text-[#44546F] font-medium"
                                            }  `}
                                        >
                                            {button.label}
                                        </p>
                                    </button>
                                ))}
                        </div>
                    </div>
                </div>

                <div className="py-4 overflow-x-visible border-b border-zinc-200">
                    <SubHeading
                        title="Button Radius"
                        subTitle="The corner radius of the button"
                        icon={
                            <div
                                className="border-t border-r border-black size-3"
                                style={{
                                    borderTopRightRadius: `${getScaledRadius(
                                        buttonRadius,
                                    )}px`,
                                }}
                            >
                                {" "}
                            </div>
                        }
                    />
                    <div className="relative flex flex-col w-full gap-2 ">
                        <Slider
                            onMouseLeave={() => setTooltipVisible(false)}
                            onMouseEnter={() => setTooltipVisible(true)}
                            defaultValue={[theme?.rounded_corners]}
                            max={MAX_RADIUS}
                            step={1}
                            onValueChange={handleButtonRadius}
                            showBadge={false}
                            placeholder={`${theme?.rounded_corners}px`}
                        />
                        <p className="flex justify-end w-full text-xs text-gray-300 ">
                            {theme?.rounded_corners}px
                        </p>
                    </div>
                </div>
                <div className="py-4 border-b border-zinc-200">
                    <SubHeading
                        title="Button Size"
                        subTitle="The size of the button"
                        icon={
                            <svg
                                width="14"
                                height="15"
                                viewBox="0 0 14 15"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <mask
                                    id="path-1-inside-1_439_4139"
                                    fill="white"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M10.7334 3.69629C10.9653 3.69629 11.1534 3.88433 11.1534 4.11629V7.84962C11.1534 8.08158 10.9653 8.26962 10.7334 8.26962C10.5014 8.26962 10.3134 8.08158 10.3134 7.84962V5.13026L4.28064 11.163H7.00001C7.23197 11.163 7.42001 11.351 7.42001 11.583C7.42001 11.8149 7.23197 12.003 7.00001 12.003H3.26668C3.15529 12.003 3.04846 11.9587 2.96969 11.88C2.89093 11.8012 2.84668 11.6943 2.84668 11.583V7.84962C2.84668 7.61766 3.03472 7.42962 3.26668 7.42962C3.49864 7.42962 3.68668 7.61766 3.68668 7.84962V10.569L9.71938 4.53629H7.00001C6.76805 4.53629 6.58001 4.34825 6.58001 4.11629C6.58001 3.88433 6.76805 3.69629 7.00001 3.69629H10.7334Z"
                                    />
                                </mask>
                                <path
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M10.7334 3.69629C10.9653 3.69629 11.1534 3.88433 11.1534 4.11629V7.84962C11.1534 8.08158 10.9653 8.26962 10.7334 8.26962C10.5014 8.26962 10.3134 8.08158 10.3134 7.84962V5.13026L4.28064 11.163H7.00001C7.23197 11.163 7.42001 11.351 7.42001 11.583C7.42001 11.8149 7.23197 12.003 7.00001 12.003H3.26668C3.15529 12.003 3.04846 11.9587 2.96969 11.88C2.89093 11.8012 2.84668 11.6943 2.84668 11.583V7.84962C2.84668 7.61766 3.03472 7.42962 3.26668 7.42962C3.49864 7.42962 3.68668 7.61766 3.68668 7.84962V10.569L9.71938 4.53629H7.00001C6.76805 4.53629 6.58001 4.34825 6.58001 4.11629C6.58001 3.88433 6.76805 3.69629 7.00001 3.69629H10.7334Z"
                                    fill="#191B33"
                                />
                                <path
                                    d="M10.3134 5.13026H11.3134V2.71605L9.60625 4.42316L10.3134 5.13026ZM4.28064 11.163L3.57354 10.4559L1.86643 12.163H4.28064V11.163ZM3.68668 10.569H2.68668V12.9832L4.39379 11.2761L3.68668 10.569ZM9.71938 4.53629L10.4265 5.2434L12.1336 3.53629H9.71938V4.53629ZM10.7334 4.69629C10.413 4.69629 10.1534 4.43666 10.1534 4.11629H12.1534C12.1534 3.332 11.5175 2.69629 10.7334 2.69629V4.69629ZM10.1534 4.11629V7.84962H12.1534V4.11629H10.1534ZM10.1534 7.84962C10.1534 7.52926 10.413 7.26962 10.7334 7.26962V9.26962C11.5175 9.26962 12.1534 8.63391 12.1534 7.84962H10.1534ZM10.7334 7.26962C11.0537 7.26962 11.3134 7.52926 11.3134 7.84962H9.31336C9.31336 8.63391 9.94918 9.26962 10.7334 9.26962V7.26962ZM11.3134 7.84962V5.13026H9.31336V7.84962H11.3134ZM9.60625 4.42316L3.57354 10.4559L4.98775 11.8701L11.0205 5.83737L9.60625 4.42316ZM4.28064 12.163H7.00001V10.163H4.28064V12.163ZM7.00001 12.163C6.67965 12.163 6.42001 11.9033 6.42001 11.583H8.42001C8.42001 10.7988 7.7843 10.163 7.00001 10.163V12.163ZM6.42001 11.583C6.42001 11.2627 6.67965 11.003 7.00001 11.003V13.003C7.7843 13.003 8.42001 12.3671 8.42001 11.583H6.42001ZM7.00001 11.003H3.26668V13.003H7.00001V11.003ZM3.26668 11.003C3.42044 11.003 3.56801 11.064 3.67684 11.1729L2.26254 12.587C2.52891 12.8534 2.89013 13.003 3.26668 13.003V11.003ZM3.67684 11.1729C3.7856 11.2817 3.84668 11.4291 3.84668 11.583H1.84668C1.84668 11.9595 1.99626 12.3207 2.26254 12.587L3.67684 11.1729ZM3.84668 11.583V7.84962H1.84668V11.583H3.84668ZM3.84668 7.84962C3.84668 8.16995 3.587 8.42962 3.26668 8.42962V6.42962C2.48243 6.42962 1.84668 7.06538 1.84668 7.84962H3.84668ZM3.26668 8.42962C2.94636 8.42962 2.68668 8.16995 2.68668 7.84962H4.68668C4.68668 7.06538 4.05093 6.42962 3.26668 6.42962V8.42962ZM2.68668 7.84962V10.569H4.68668V7.84962H2.68668ZM4.39379 11.2761L10.4265 5.2434L9.01228 3.82918L2.97957 9.86189L4.39379 11.2761ZM9.71938 3.53629H7.00001V5.53629H9.71938V3.53629ZM7.00001 3.53629C7.32034 3.53629 7.58001 3.79597 7.58001 4.11629H5.58001C5.58001 4.90053 6.21577 5.53629 7.00001 5.53629V3.53629ZM7.58001 4.11629C7.58001 4.43661 7.32034 4.69629 7.00001 4.69629V2.69629C6.21577 2.69629 5.58001 3.33204 5.58001 4.11629H7.58001ZM7.00001 4.69629H10.7334V2.69629H7.00001V4.69629Z"
                                    fill="#191B33"
                                    mask="url(#path-1-inside-1_439_4139)"
                                />
                            </svg>
                        }
                    />
                    <div className="mt-4 rounded-lg bg-zinc-100">
                        <div className="grid grid-cols-3 p-1 h-9">
                            {buttonSizes.map((button) => (
                                <button
                                    key={button?.value}
                                    className={` ${tabsStyles} ${
                                        button.value === buttonSize
                                            ? "bg-white"
                                            : ""
                                    }`}
                                    onClick={() =>
                                        handleButtonSize(button?.value)
                                    }
                                >
                                    <p
                                        className={` text-xs ${
                                            button.value === buttonSize
                                                ? "text-[#191B33]  font-semibold"
                                                : " text-[#44546F] font-medium"
                                        }  `}
                                    >
                                        {button.label}
                                    </p>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="py-4 border-b border-zinc-200">
                    <SubHeading
                        title={"Button Text Bold"}
                        subTitle={"Make the text bold"}
                        icon={<img src={fontsize} alt="bold" />}
                    />
                    <div className="mt-4 rounded-lg bg-zinc-100">
                        <div className="grid grid-cols-2 p-1 h-9">
                            {buttonTextStyles.map((button) => (
                                <button
                                    key={button.value}
                                    className={` ${tabsStyles} ${
                                        button.value === theme?.is_bold
                                            ? "bg-white"
                                            : ""
                                    }`}
                                    onClick={() =>
                                        handleButtonTextBold(button.value)
                                    }
                                >
                                    <p
                                        className={`text-xs ${
                                            button.value === theme?.is_bold
                                                ? "text-[#191B33]  font-semibold"
                                                : " text-[#44546F] font-medium"
                                        }  `}
                                    >
                                        {button?.label}
                                    </p>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Custom branding */}
                <div className="flex flex-col gap-2 py-4 ">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1">
                            <svg
                                width="15"
                                height="14"
                                viewBox="0 0 15 14"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M13.3581 5.25L11.0247 1.75H4.02474L1.69141 5.25M13.3581 5.25L7.52474 12.8333M13.3581 5.25H1.69141M7.52474 12.8333L1.69141 5.25M7.52474 12.8333L9.85807 5.25L8.10807 1.75M7.52474 12.8333L5.19141 5.25L6.94141 1.75"
                                    stroke="#191B33"
                                    stroke-width="0.8"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>

                            <Label>Custom Branding</Label>
                        </div>
                    </div>
                    <div className="flex justify-start w-full text-start">
                        <Label className="text-xs font-normal text-[#44546F]">
                            Change the visibility of the
                            <Label className="text-xs font-medium text-primary">
                                {" "}
                                "Built with Buildform"{" "}
                            </Label>{" "}
                            branding or replace it with your own logo
                        </Label>
                    </div>

                    <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-1 mt-1">
                            <div className="p-0 rounded-[8px] flex items-center justify-center border size-9 border-[#DCDFE4] ">
                                {brandingImage ? (
                                    <img
                                        src={brandingImage}
                                        alt="branding"
                                        className="w-full object-contain h-full size-9 rounded-[8px]"
                                    />
                                ) : (
                                    <ImagePlus
                                        className=" text-[#b1b2b4]"
                                        size={16}
                                    />
                                )}
                            </div>

                            {brandingImage ? (
                                <Button
                                    onClick={handleRemoveCustomBrandingImage}
                                    variant="icon"
                                    size="sm"
                                >
                                    <Trash2
                                        size={16}
                                        className="text-[#8590A2] "
                                    />
                                </Button>
                            ) : (
                                <div
                                    className={
                                        " h-9 rounded-[8px] max-w-[244px] px-3 py-2  border border-[#DCDFE4] w-full"
                                    }
                                >
                                    <div
                                        className="flex items-center justify-between hover:cursor-pointer"
                                        onClick={handleFileInputClick}
                                    >
                                        <p className=" text-start text-xs text-[#8590A2]">
                                            Size limit (10MB max)
                                        </p>
                                        <UploadCloud
                                            className=" text-[#DCDFE4]"
                                            size={20}
                                        />
                                        <input
                                            ref={fileInputRef}
                                            className="hidden"
                                            accept="image/png, image/jpeg, image/jpg"
                                            type="file"
                                            onChange={handleFileChange}
                                            disabled={usersPlan === "free"}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                        {brandingImageError && (
                            <p className="w-full text-xs text-red-500 text-start">
                                {brandingImageError}
                            </p>
                        )}
                    </div>
                </div>

                {/* Buildform branding */}
                <div className="flex flex-col gap-2 py-4 ">
                    <div className="flex items-center justify-between ">
                        <div className="flex items-center gap-1">
                            <svg
                                width="15"
                                height="14"
                                viewBox="0 0 15 14"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M2.94847 2.28428H12.1023C12.2531 2.28428 12.3754 2.40654 12.3754 2.55735V3.90351C12.3754 4.05432 12.2531 4.17658 12.1023 4.17658H2.94847C2.79765 4.17658 2.67539 4.05432 2.67539 3.90351V2.55735C2.67539 2.40654 2.79765 2.28428 2.94847 2.28428Z"
                                    stroke="black"
                                    stroke-width="0.8"
                                />
                                <path
                                    d="M2.94847 6.05356H8.4677C8.61852 6.05356 8.74078 6.17582 8.74078 6.32664V7.6728C8.74078 7.82361 8.61852 7.94587 8.4677 7.94587H2.94847C2.79765 7.94587 2.67539 7.82361 2.67539 7.6728V6.32664C2.67539 6.17583 2.79765 6.05356 2.94847 6.05356Z"
                                    stroke="black"
                                    stroke-width="0.8"
                                />
                                <path
                                    d="M5.50608 9.8231H12.1022C12.2531 9.8231 12.3753 9.94536 12.3753 10.0962V11.4423C12.3753 11.5931 12.2531 11.7154 12.1022 11.7154H5.50608C5.35527 11.7154 5.23301 11.5931 5.23301 11.4423V10.0962C5.23301 9.94536 5.35527 9.8231 5.50608 9.8231Z"
                                    stroke="black"
                                    stroke-width="0.8"
                                />
                            </svg>

                            <Label>Buildform Branding</Label>
                            <Badge className=" w-[37px] h-[15px] px-2 py-1 hover:bg-[#DBEAFE] text-[#2563EB] text-center text-[10px] font-bold bg-[#DBEAFE] ">
                                PRO
                            </Badge>
                        </div>

                        <Switch
                            checked={showBuildformBranding}
                            onCheckedChange={handleRemoveBranding}
                        />
                    </div>

                    <div className="flex justify-start w-full text-start">
                        <Label className="text-xs font-normal text-[#44546F]">
                            Change the visibility of the
                            <Label className="text-xs font-medium text-primary">
                                {" "}
                                "Built with Buildform"{" "}
                            </Label>{" "}
                        </Label>
                    </div>
                </div>
                <div className="flex justify-start w-full py-4 pb-20 mt-auto mb-4 ">
                    <FormSettingsModal />
                </div>
            </div>
        </div>
    );
};

export default DesignTab;

const SubHeading = ({ icon, title, subTitle }) => {
    return (
        <div className="mb-4">
            <div className="flex items-center gap-1 mb-1">
                <div className="w-[14px] h-[14px] -mt-0.5 ">{icon}</div>
                <h4 className="text-sm font-semibold text-[#191B33] ">
                    {title}
                </h4>
            </div>
            <p className="text-xs text-start text-[#44546F]">{subTitle}</p>
        </div>
    );
};
